/**
 * Pure URL builders shared by the row dropdown
 * (``InvoiceActionMenu``), the detail-page button row
 * (``InvoiceActionButtons``), and the list-page bulk toolbar
 * (``InvoiceBulkActionBar``). Keeping the definitions in one place
 * avoids the 3-way drift hazard noted in the invoice-group-actions
 * code review.
 */

/**
 * Make a path absolute against the app's own origin.
 *
 * A *copied* link must be absolute or it is useless when pasted, while an
 * ``href`` attribute is happiest relative — so the builders below stay
 * relative and the call sites that copy wrap them in this. SSR-safe: the
 * origin is ``""`` on the server, where there is no window and nothing is
 * being copied anyway.
 */
export function absoluteUrl(path: string): string {
  const origin = typeof window === "undefined" ? "" : window.location.origin
  return `${origin}${path}`
}

export function payUrlFor(invoiceId: string): string {
  return absoluteUrl(`/pay/${invoiceId}`)
}

/**
 * The customer self-service portal link a Manager copies from the invoice
 * (docs/design/customer-portal.md). Carries the ``invoice`` id — not the raw
 * email — so the pasted URL leaks no PII; the portal resolves the address
 * server-side and emails the customer a one-time sign-in link.
 */
export function portalUrlFor(invoiceId: string): string {
  return absoluteUrl(`/portal?invoice=${encodeURIComponent(invoiceId)}`)
}

export function pdfUrlFor(invoiceId: string): string {
  // Trailing slash before the query string matches the Ninja-registered
  // route and the Next.js dev-proxy rewrite (which appends ``/`` to
  // every ``/api/*`` forward — without the slash, the request falls
  // through to ``/invoices/{invoice_id}/`` and 422s).
  return `/api/invoices/invoice.pdf/?invoice_id=${encodeURIComponent(invoiceId)}`
}

/**
 * The whole-invoice receipt path. Anonymous: the invoice UUID is the
 * credential, the same as for every other receipt link in the product —
 * the route is deliberately session-free so a document the customer
 * already holds keeps working (including after a refund).
 *
 * The backend gates it on ``RECEIPT_AVAILABLE_STATUSES`` and refuses it
 * for a deposit invoice (which demands a ``stage``), so callers surface
 * it through ``receiptActionFor`` rather than a bare status check.
 */
export function receiptPathFor(invoiceId: string): string {
  // Trailing slash before the query string, same as ``pdfUrlFor``.
  return `/api/invoices/receipt.pdf/?invoice_id=${encodeURIComponent(invoiceId)}`
}

/**
 * Per-leg deposit receipt path. The backend gates this on the
 * leg being ``paid`` (deposit-payments spec §6.7); callers must only
 * surface it once ``leg.status === "paid"``. ``stage`` is
 * ``"deposit"`` | ``"balance"``.
 */
export function depositReceiptPathFor(
  invoiceId: string,
  stage: "deposit" | "balance",
): string {
  // Trailing slash before the query string, same as ``pdfUrlFor`` — the
  // route is registered as ``receipt.pdf/`` and the dev proxy appends
  // ``/`` to ``/api/*`` forwards; without it the request falls through.
  return `${receiptPathFor(invoiceId)}&stage=${encodeURIComponent(stage)}`
}

/**
 * Per-charge installment receipt path — the receipt for charge
 * ``sequence`` of a plan.
 *
 * The backend resolves it row-by-row (a settled ``PAID`` row), never off
 * the invoice status, and 404s an unmatched ``sequence`` rather than
 * falling back to the whole-invoice receipt. Callers surface it only on
 * a ``paid`` schedule row.
 */
export function installmentReceiptPathFor(
  invoiceId: string,
  sequence: number,
): string {
  return `${receiptPathFor(invoiceId)}&sequence=${encodeURIComponent(sequence)}`
}

/**
 * The customer-facing per-leg pay URL: ``/pay/{id}/{stage}``
 * (deposit-payments spec §5.3). The rep copies both legs' links from
 * the deposit panel.
 */
export function depositPayUrlFor(
  invoiceId: string,
  stage: "deposit" | "balance",
): string {
  // Compose the per-leg path onto the shared one-shot pay URL so the
  // SSR-safe origin handling lives in one place (``payUrlFor``).
  return `${payUrlFor(invoiceId)}/${stage}`
}
